import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { dashboard, alerts as alertsApi, watchedUrls as watchedUrlsApi } from "@/lib/api";
import type { DashboardStats, Alert, WatchedUrl } from "@/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { AlertSummary } from "@/components/alerts/AlertSummary";
import { DashboardStatsCard } from "@/components/dashboard/DashboardStats";
import { MiraPanel } from "@/components/dashboard/MiraPanel";
import { GettingStarted } from "@/components/dashboard/GettingStarted";
import { SignalDot } from "@/components/ui/landing-primitives";
import { KiteMark } from "@/components/ui/landing-primitives";
import { timeAgo, pageTypeLabel, getDomainFromUrl } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [watchedUrls, setWatchedUrls] = useState<WatchedUrl[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const loadRequestId = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++loadRequestId.current;
    setIsLoading(true);
    setStats(null);
    setRecentAlerts([]);
    setWatchedUrls([]);
    try {
      const [statsRes, alertsRes, urlsRes] = await Promise.all([
        dashboard.stats(),
        alertsApi.list({ limit: 8 }),
        watchedUrlsApi.list(),
      ]);
      if (requestId !== loadRequestId.current) return;
      if ("data" in statsRes && statsRes.data) setStats(statsRes.data);
      if ("data" in alertsRes && alertsRes.data) setRecentAlerts(alertsRes.data.alerts);
      if ("data" in urlsRes && urlsRes.data) setWatchedUrls(urlsRes.data);
    } catch (e) {
      console.error("[Dashboard] load exception:", e);
    } finally {
      if (requestId === loadRequestId.current) setIsLoading(false);
    }
  }, []);

  // Refresh data when workspace changes
  useWorkspaceRefresh(load);

  return (
    <div className="flex h-full flex-col gap-0">
      <div className="relative shrink-0">
        <PageHeader
          title="Overview"
          actions={
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Icon name="search-01" size={15} />
              </Button>
            </>
          }
        />
        <KiteMark className="bottom-[-6px] right-[calc(18rem_-_6.5px)] hidden xl:block" />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main content */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">

          {/* First-run checklist (hides itself when done) */}
          <GettingStarted />

          {/* Stats */}
          <DashboardStatsCard stats={stats} isLoading={isLoading} />

          {/* Mira: chat and competitor discovery */}
          <MiraPanel />

          {/* Intelligence Feed — pipeline style */}
          <div>
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="file-01" size={12} className="text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Intelligence Feed
                </span>
              </div>
              <Link
                to="/alerts"
                className="group flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
              >
                View all
                <Icon name="arrow-right-01" size={11} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>

            {isLoading ? (
              <div className="space-y-0">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex gap-4 py-4">
                    <div className="flex flex-col items-center gap-0 shrink-0 w-5">
                      <Skeleton className="h-3 w-3 rounded-sm" />
                      {i < 3 && <div className="w-px flex-1 bg-border mt-1" />}
                    </div>
                    <div className="flex-1 space-y-2 pb-4">
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentAlerts.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-14 text-center">
                <Icon name="radar-01" size={28} className="text-muted-foreground/20" />
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">No alerts yet</p>
                <p className="text-[11px] text-muted-foreground font-sans max-w-xs">
                  Add competitors to your watchlist and we'll surface changes here.
                </p>
                <Button size="sm" asChild>
                  <Link to="/watchlist">Add competitor</Link>
                </Button>
              </div>
            ) : (
              <div>
                {recentAlerts.map((alert, i) => (
                  <PipelineItem
                    key={alert.id}
                    alert={alert}
                    isLast={i === recentAlerts.length - 1}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Activity chart — custom div bars */}
          <ActivityChart data={stats?.alertsLast7Days ?? []} isLoading={isLoading} />
        </div>

        {/* Right panel — pipeline style */}
        <div className="hidden w-72 shrink-0 flex-col overflow-y-auto border-l border-border p-5 xl:flex">

          <div className="relative -mx-5 mb-5 flex items-center justify-between border-b border-border px-5 pb-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Monitoring Pipeline</span>
            <Link to="/watchlist" className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">+ Add</Link>
          </div>

          {isLoading ? (
            <div className="space-y-0">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-3 py-3">
                  <div className="flex flex-col items-center shrink-0 w-4">
                    <Skeleton className="h-3 w-3 rounded-sm" />
                    {i < 4 && <div className="w-px flex-1 bg-border mt-1" />}
                  </div>
                  <div className="flex-1 space-y-1.5 pb-3">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-2.5 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : watchedUrls.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Icon name="radar-01" size={20} className="text-muted-foreground/20" />
              <p className="font-mono text-[11px] text-muted-foreground">No URLs monitored</p>
              <Link to="/watchlist" className="font-mono text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors">
                <span className="inline-flex items-center gap-1">Add to watchlist<Icon name="arrow-right-01" size={11} /></span>
              </Link>
            </div>
          ) : (
            <MonitoringPipeline watchedUrls={watchedUrls} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Monitoring pipeline ──────────────────────────────────────────────────────

export function MonitoringPipeline({ watchedUrls }: { watchedUrls: WatchedUrl[] }) {
  return (
    <div>
      {watchedUrls.slice(0, 10).map((wu, i) => {
        const isLast = i === Math.min(watchedUrls.length, 10) - 1;
        const nodeColor =
          wu.lastStatus === "success" ? "bg-emerald-500 border-emerald-500" :
          wu.lastStatus === "failed" || wu.lastStatus === "blocked" ? "bg-red-500 border-red-500" :
          wu.lastStatus === "running" ? "bg-amber-500 border-amber-500" :
          "bg-transparent border-border";
        const lineColor =
          wu.lastStatus === "success" ? "bg-emerald-500/20" :
          wu.lastStatus === "failed" || wu.lastStatus === "blocked" ? "bg-red-500/20" :
          "bg-border/50";

        return (
          <div key={wu.id} className="flex gap-3 group hover:bg-accent/20 transition-colors rounded -mx-1 px-1">
            <div className="flex flex-col items-center shrink-0 w-4 pt-3">
              <div className={cn(
                "h-2.5 w-2.5 rounded-sm border-2 shrink-0 transition-all group-hover:scale-110",
                nodeColor,
                wu.lastStatus === "running" && "animate-pulse"
              )} />
              {!isLast && <div className={cn("w-px flex-1 mt-1 min-h-[16px]", lineColor)} />}
            </div>
            <div className={cn("flex-1 min-w-0 py-2.5", !isLast && "pb-3")}>
              <p className="font-sans text-xs truncate text-foreground">
                {getDomainFromUrl(wu.url)}
                <span className="text-muted-foreground/60"> · {wu.label}</span>
              </p>
              <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
                {wu.lastCrawledAt ? timeAgo(wu.lastCrawledAt) : "pending"}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Activity chart ───────────────────────────────────────────────────────────

export function ActivityChart({ data, isLoading }: { data: { date: string; count: number }[]; isLoading: boolean }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const max = Math.max(...data.map(d => d.count), 1);

  useEffect(() => {
    if (!isLoading && data.length > 0) {
      // Trigger grow animation after a short delay
      const t = setTimeout(() => setMounted(true), 50);
      return () => clearTimeout(t);
    }
  }, [isLoading, data]);

  return (
    <div className="rounded border border-border p-5">
      <div className="flex items-center justify-between pb-4 mb-5 border-b border-border -mx-5 px-5">
        <div className="flex items-center gap-2">
          <Icon name="activity-01" size={12} className="text-muted-foreground" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Alert activity — 7 days
          </span>
        </div>
        {hovered !== null && data[hovered] && (
          <span className="font-mono text-[11px] text-foreground">
            {data[hovered].date} · <span className="font-bold">{data[hovered].count}</span> alert{data[hovered].count !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-end gap-1.5 h-36">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="flex-1 rounded-sm" style={{ height: `${30 + Math.random() * 50}%` }} />
          ))}
        </div>
      ) : (
        <div className="flex items-end gap-1.5 h-36">
          {data.map((d, i) => {
            const pct = max === 0 ? 0 : (d.count / max) * 100;
            const isHov = hovered === i;
            return (
              <div
                key={d.date}
                className="flex-1 flex flex-col items-center gap-1 cursor-pointer group"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="w-full flex items-end" style={{ height: "80px" }}>
                  <div
                    className="w-full rounded-sm transition-all duration-200"
                    style={{
                      height: mounted ? `${Math.max(pct, d.count > 0 ? 8 : 3)}%` : "3%",
                      transition: `height 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 60}ms`,
                      background: isHov
                        ? "hsl(var(--foreground) / 0.9)"
                        : d.count > 0
                        ? "hsl(var(--foreground) / 0.6)"
                        : "hsl(var(--foreground) / 0.1)",
                    }}
                  />
                </div>
                <span className="font-mono text-[11px] text-muted-foreground/60 group-hover:text-muted-foreground transition-colors">
                  {d.date.slice(-2)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Pipeline feed item ───────────────────────────────────────────────────────

const signalColors = {
  high: { node: "bg-red-500 border-red-500", line: "bg-red-500/20" },
  medium: { node: "bg-amber-500 border-amber-500", line: "bg-amber-500/20" },
  low: { node: "bg-zinc-400 border-zinc-400", line: "bg-zinc-400/20" },
};

export function PipelineItem({ alert, isLast }: { alert: Alert; isLast: boolean }) {
  const colors = signalColors[alert.signalScore] ?? signalColors.low;

  return (
    <Link
      to={`/alerts/${alert.id}`}
      className="flex gap-4 group hover:bg-accent/30 transition-colors rounded px-2 -mx-2"
    >
      {/* Pipeline track */}
      <div className="flex flex-col items-center shrink-0 w-5 pt-4">
        {/* Node */}
        <SignalDot tone={alert.signalScore} title={`${alert.signalScore} signal`} className="size-3 transition-transform group-hover:scale-110" />
        {/* Connector line */}
        {!isLast && (
          <div className={cn("w-px flex-1 mt-1 min-h-[24px]", colors.line)} />
        )}
      </div>

      {/* Content */}
      <div className={cn("flex-1 min-w-0 py-3", !isLast && "pb-4")}>
        {/* Meta row */}
        <div className="flex items-center gap-2 mb-1">
          {alert.competitor && (
            <span className="font-mono text-[11px] uppercase tracking-widest text-foreground font-semibold">
              {alert.competitor.name}
            </span>
          )}
          <span className="text-muted-foreground font-mono text-[11px]">·</span>
          <span className="font-mono text-[11px] text-muted-foreground">
            {pageTypeLabel(alert.pageType)}
          </span>
          {alert.status === "unread" && (
            <span className="h-1.5 w-1.5 rounded-full bg-foreground shrink-0" />
          )}
          <span className="ml-auto font-mono text-[11px] text-muted-foreground shrink-0">
            {timeAgo(alert.detectedAt)}
          </span>
        </div>

        {/* Summary */}
        <AlertSummary summary={alert.summary} className="font-mono text-[11px] text-foreground/60 line-clamp-2 leading-relaxed" />

        {/* URL */}
        {alert.watchedUrl && (
          <p className="mt-1 font-mono text-[11px] text-muted-foreground/60 truncate">
            {alert.watchedUrl.url}
          </p>
        )}
      </div>
    </Link>
  );
}
